import React, { Component } from "react";
import { connect } from "react-redux";

// Components
import TitleBar from "./TitleBar";
import ProfileTable from "./ProfileTable";
import ProfileTablePersonal from "./profile-table/ProfileTablePersonal";
import ProfileTableLocation from "./profile-table/ProfileTableLocation";
import ProfileTableWorkLocation from "./profile-table/ProfileTableWorkLocation";
import ProfileTableEmergencyContact from "./profile-table/ProfileTableEmergencyContact";
import ProfileTableBio from "./profile-table/ProfileTableBio";
import UpdateFieldsConfirmationDialog from "./UpdateFieldsConfirmationDialog";
import Button from "../../utils/Button";
import StickyFooter from "../../utils/StickyFooter";

// CSS
import "../../../css/forms/react-select.css";

// State
import { getReviewerOptions } from "../../../store/actions/reviewers";
import {
	updatePersonalInfoFields,
	updatePersonalFieldsClearStatus
} from "../../../store/actions/reviewers";
import { resetUpdateAccountSuccess } from "../../../store/actions/user";

// Utils
import { formatMomentObject, formatMomentString } from "../../../utils/dateHelpers";

class PersonalInfoForm extends Component {
	constructor(props) {
		super(props);
		this.state = {
			userData: !!props.user.id ? this.formatRecordData(props.user) : props.user,
			showUpdateConfirmationDialog: false,
			canSubmitInfo: true
		};
	}

	formatRecordData = data => {
		// Ensure dates are valid
		const date_added = formatMomentString(data.date_added);
		const date_edited = formatMomentString(data.date_added);
		const cca_training = formatMomentObject(data.cca_training);
		const last_next_review = formatMomentObject(data.last_next_review);

		// React doesn't like it when input elements have values of null, so set them to empty
		Object.keys(data).map(function(key) {
			if (data[key] === null && key !== "last_next_review" && key !== "cca_training") {
				data[key] = "";
			}

			if (key === "alternate_emails" && !data[key].length) {
				data[key] = [""];
			}

			// Return something just so browser doesn't throw a warning about map not returning anything
			return data[key];
		});

		return { ...data, date_added, date_edited, last_next_review, cca_training };
	};

	// ===================================================================================
	//  LIFE CYCLE
	// ===================================================================================
	componentDidMount() {
		this.props.getReviewerOptions();
	}

	// ===================================================================================
	//  INPUTS
	// ===================================================================================
	updateSelectedValue = (value, id) => {
		this.setState({
			userData: {
				...this.state.userData,
				[id]: value
			}
		});
	};

	handleKeyPress = event => {
		if (event.key === "Enter") {
			event.preventDefault();
			this.submitConfirm();
		}
	};

	handleInputChange = event => {
		const { id, value } = event.target;
		this.setState({
			userData: {
				...this.state.userData,
				[id]: value
			}
		});
	};

	handleRadioClick = (event, name) => {
		const { value } = event.target;
		this.setState({
			userData: {
				...this.state.userData,
				[name]: value
			}
		});
	};

	editorChange = (id, evt) => {
		const newContent = evt.editor.getData();
		const newData = {
			...this.state.userData,
			[id]: newContent
		};

		this.setState({ userData: newData });
	};

	// ===================================================================================
	//  INPUT COLLECTION HANDLING
	// ===================================================================================
	handleInputChangeArray = (event, index) => {
		const { name, value } = event.target;

		this.setState({
			userData: {
				...this.state.userData,
				[name]: this.state.userData[name].map((item, i) => {
					if (i !== index) {
						return item;
					}

					return value;
				})
			}
		});
	};

	addInputToCollection = index => {
		this.setState({
			userData: {
				...this.state.userData,
				[index]: [...this.state.userData[index], ""]
			}
		});
	};

	deleteInputFromCollection = (name, index) => {
		this.setState({
			userData: {
				...this.state.userData,
				[name]: [
					...this.state.userData[name].slice(0, index),
					...this.state.userData[name].slice(index + 1)
				]
			}
		});
	};

	// ===================================================================================
	//  SECTOR-SPECIFIC INPUT HANDLING
	// ===================================================================================
	handleInputChangeSector = (event, id) => {
		let { value } = event.target;

		// Value should be treated as an integer
		value = parseInt(value, 10);

		// Clone array
		let newState = [...this.state.userData.sectors];

		// Find index of the id within the cloned array
		const indexOf = newState.findIndex(item => item.id === id);

		// If not found, bail
		if (indexOf < 0) return;

		newState[indexOf].years = value;

		this.setState({
			userData: {
				...this.state.userData,
				sectors: [...newState]
			}
		});
	};

	handleCheckboxClickSector = (event, name) => {
		let { value } = event.target;

		// Value should be treated as an integer
		value = parseInt(value, 10);

		// Clone array
		let newState = [...this.state.userData[name]];

		// Find index of the id within the cloned array
		const indexOf = newState.findIndex(item => item.id === value);

		// If index was not found (-1) then we should immutably concat() the a new object onto the array
		if (indexOf < 0) {
			newState = newState.concat({
				id: value,
				years: null
			});
		}
		// The index was found, so let's remove it immutably
		else {
			newState = newState.slice(0, indexOf).concat(newState.slice(indexOf + 1));
		}

		this.setState({
			userData: {
				...this.state.userData,
				[name]: [...newState]
			}
		});
	};

	// ===================================================================================
	//  DIALOGS
	// ===================================================================================
	submitConfirm = () => {
		this.setState({ showUpdateConfirmationDialog: true });
	};

	closeDialog = () => {
		// Clear success statuses
		this.props.updatePersonalFieldsClearStatus();
		this.props.resetUpdateAccountSuccess();

		// Reset local state
		this.setState({
			showUpdateConfirmationDialog: false,
			showDialogErrors: null,
			canSubmitInfo: true
		});
	};

	// ===================================================================================
	//  SUBMIT
	// ===================================================================================
	performSubmit = data => {
		this.setState(
			{
				showDialogErrors: true,
				canSubmitInfo: false
			},
			() => {
				this.props.updatePersonalInfoFields(data);
			}
		);
	};

	// ===================================================================================
	//  RENDER
	// ===================================================================================
	render() {
		let { userData } = this.state;
		let { translations } = this.props;

		return (
			<div className="profile-section">
				<StickyFooter>
					<div className="sticky-footer--text">
						{translations.sections.reviewer_form.update_label}
					</div>
					<Button text="Update" clickHandler={this.submitConfirm} />
				</StickyFooter>

				<TitleBar>{translations.sections.reviewer_form.personal_info_title}</TitleBar>
				<div className="profile-table-wrapper">
					<ProfileTable>
						<ProfileTablePersonal
							{...this.props}
							user={userData}
							handleKeyPress={this.handleKeyPress}
							handleInputChange={this.handleInputChange}
							updateSelectedValue={this.updateSelectedValue}
							handleRadioClick={this.handleRadioClick}
							handleInputChangeArray={this.handleInputChangeArray}
							addInputToCollection={this.addInputToCollection}
							deleteInputFromCollection={this.deleteInputFromCollection}
						/>
					</ProfileTable>
				</div>

				<TitleBar>{translations.sections.reviewer_form.location_title}</TitleBar>
				<div className="profile-table-wrapper">
					<ProfileTable>
						<ProfileTableLocation
							{...this.props}
							user={userData}
							handleKeyPress={this.handleKeyPress}
							handleInputChange={this.handleInputChange}
							updateSelectedValue={this.updateSelectedValue}
						/>
					</ProfileTable>
				</div>

				<TitleBar>{translations.sections.reviewer_form.work_location_title}</TitleBar>
				<div className="profile-table-wrapper">
					<ProfileTable>
						<ProfileTableWorkLocation
							{...this.props}
							user={userData}
							handleKeyPress={this.handleKeyPress}
							handleInputChange={this.handleInputChange}
							updateSelectedValue={this.updateSelectedValue}
						/>
					</ProfileTable>
				</div>

				<TitleBar>{translations.sections.reviewer_form.emergency_contact_title}</TitleBar>
				<div className="profile-table-wrapper">
					<ProfileTable>
						<ProfileTableEmergencyContact
							{...this.props}
							user={userData}
							handleKeyPress={this.handleKeyPress}
							handleInputChange={this.handleInputChange}
						/>
					</ProfileTable>
				</div>

				<TitleBar>{translations.sections.reviewer_form.bio_experience_title}</TitleBar>
				<div className="profile-table-wrapper">
					<ProfileTable>
						<ProfileTableBio
							{...this.props}
							user={userData}
							handleKeyPress={this.handleKeyPress}
							handleInputChange={this.handleInputChange}
							editorChange={this.editorChange}
							handleCheckboxClickSector={this.handleCheckboxClickSector}
							handleInputChangeSector={this.handleInputChangeSector}
						/>
					</ProfileTable>
				</div>
				<UpdateFieldsConfirmationDialog
					show={this.state.showUpdateConfirmationDialog}
					user={this.state.userData}
					inProgress={this.props.personalFieldsLoading}
					itemTitle={`${userData.fname} ${userData.lname}`}
					handleAction={this.performSubmit}
					handleClose={() => this.closeDialog()}
					showDialogErrors={this.state.showDialogErrors}
					errors={this.props.personalFieldsErrors}
					success={this.props.personalFieldsSuccess}
					canSubmitInfo={this.state.canSubmitInfo}
					dialogTitle={translations.sections.reviewer_form.update_verification_title}
					btnTranslations={translations.sections.buttons}
				>
					{translations.sections.reviewer_form.update_verification_message}
				</UpdateFieldsConfirmationDialog>
			</div>
		);
	}
}

const mapStateToProps = state => {
	return {
		admin: state.user.admin,
		reviewerOptionsLoading: state.reviewers.reviewerOptionsLoading,
		reviewerOptionsError: state.reviewers.reviewerOptionsError,
		reviewerOptions: state.reviewers.reviewerOptions,
		personalFieldsLoading: state.reviewers.personalFieldsLoading,
		personalFieldsErrors: state.reviewers.personalFieldsErrors,
		personalFieldsSuccess: state.reviewers.personalFieldsSuccess
	};
};

export default connect(
	mapStateToProps,
	{
		getReviewerOptions,
		updatePersonalInfoFields,
		updatePersonalFieldsClearStatus,
		resetUpdateAccountSuccess
	}
)(PersonalInfoForm);



// WEBPACK FOOTER //
// ./src/components/reviewer/profile/PersonalInfoForm.js